/**
 * Feature Flag Registry
 * -----------------------------------------------------------------------------
 * This file is the single source of truth for all feature flags in the app.
 * Each flag entry defines:
 *   - `default`: fallback value when not overridden by ECS or other config
 *   - `description`: explanation of what the flag does
 *
 * To add a new flag, add it here. Types and defaults are derived automatically.
 */

export const FEATURE_FLAG_REGISTRY = {
  enableAuthenticatorSetup: {
    default: true,
    description: "Enables Microsoft Authenticator setup flow in the onboarding process.",
  },
  enableCompanyPortal: {
    default: true,
    description: "Enables Company Portal installation option for device management.",
  },
  enablePreviewTag: {
    default: false,
    description: "Displays the Preview tag in the experience for pre-release visibility.",
  },
  killSwitch: {
    default: false,
    description: "Emergency kill switch for the entire application. When true, shows maintenance screen.",
  },
  retryMaxAttempts: {
    default: 3,
    description: "Maximum number of retry attempts for provisioning requests (HTTP 424).",
  },
  retryDelayMs: {
    default: 20000,
    description: "Delay in milliseconds between retry attempts for provisioning requests.",
  },
  // Add new flags here following the same pattern
  // newFeatureFlag: {
  //   default: false,
  //   description: "Description of what this flag controls.",
  // },
} as const;

/**
 * Union type of all feature flag names.
 */
export type FeatureFlag = keyof typeof FEATURE_FLAG_REGISTRY;

/**
 * List of all feature flags as an array.
 */
export const FEATURE_FLAGS = Object.keys(FEATURE_FLAG_REGISTRY) as FeatureFlag[];

/**
 * Type mapping each flag name to its value type (inferred from registry default).
 */
export type FeatureFlagTypes = {
  [K in FeatureFlag]: (typeof FEATURE_FLAG_REGISTRY)[K]["default"];
};

/**
 * Get the default value for a specific feature flag.
 */
export function getFeatureFlagDefault<K extends FeatureFlag>(flag: K): FeatureFlagTypes[K] {
  return FEATURE_FLAG_REGISTRY[flag].default as FeatureFlagTypes[K];
}

/**
 * Get the description for a specific feature flag.
 */
export function getFeatureFlagDescription<K extends FeatureFlag>(flag: K): string {
  return FEATURE_FLAG_REGISTRY[flag].description;
}

/**
 * Get all feature flag information.
 */
export function getAllFeatureFlags(): Array<{
  name: FeatureFlag;
  default: boolean | number;
  description: string;
}> {
  return FEATURE_FLAGS.map(flag => ({
    name: flag,
    default: FEATURE_FLAG_REGISTRY[flag].default,
    description: FEATURE_FLAG_REGISTRY[flag].description,
  }));
}